import gql from 'graphql-tag';
import { actionPlanTaskFields } from './ActionPlanTaskFields';
import {
  ActionPlanTaskSchema,
  ActionPlanTasksSchema,
} from '../../definitions/schemas';
import { ActionPlanTaskStatus } from '../../enums/ActionPlans';
import { trailLogFields } from '../TrailLogs/TrailLogFields';
import Interface from '../Interface';

interface TaskOwnerInput {
  ownerType: string;
  ownerId: string;
}

export interface CreateActionPlanTaskInput {
  actionPlanId: string;
  description?: string;
  dueDate?: string;
  milestone: boolean;
  name: string;
  owners?: TaskOwnerInput[];
  parentTaskId?: string;
  previousTaskId?: string;
}

interface CreateActionPlanTaskData {
  createActionPlanTask: {
    taskPointerConflict: boolean;
    task: ActionPlanTaskSchema;
  };
}

export interface UpdateActionPlanTaskInput
  extends Omit<
    CreateActionPlanTaskInput,
    'actionPlanId' | 'parentTaskId' | 'previousTaskId'
  > {
  completePendingSubtasks?: boolean;
  id: string;
  revision: number;
  status: ActionPlanTaskStatus;
}

interface UpdateActionPlanTaskData {
  updateActionPlanTask: {
    task: ActionPlanTaskSchema;
  };
}

interface UpdateActionPlanTaskStatusInput {
  id: string;
  completePendingSubtasks: boolean;
  status: ActionPlanTaskStatus;
}

interface UpdateActionPlanTaskStatusData {
  updateActionPlanTaskStatus: {
    task: ActionPlanTaskSchema;
  };
}

interface DeleteActionPlanTaskInput {
  id: string;
}

interface ReorderActionPlanTaskInput {
  id: string;
  previousTaskId?: string;
}

interface ReorderActionPlanTaskData {
  reorderActionPlanTask: {
    tasks: ActionPlanTasksSchema;
  };
}

interface GetActionPlanTaskInput {
  id: string;
}

interface GetActionPlanTaskData {
  getActionPlanTask: ActionPlanTaskSchema;
}

export default class ActionPlansTasksInterface extends Interface {
  public async create(input: CreateActionPlanTaskInput) {
    return this.mutate<CreateActionPlanTaskData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createActionPlanTask($input: CreateActionPlanTaskInput!) {
            createActionPlanTask(input: $input) {
              errors
              message
              taskPointerConflict
              task {
                ${actionPlanTaskFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error creating task');
  }

  public async update(input: UpdateActionPlanTaskInput) {
    return this.mutate<UpdateActionPlanTaskData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateActionPlanTask($input: UpdateActionPlanTaskInput!) {
            updateActionPlanTask(input: $input) {
              errors
              message
              task {
                ${actionPlanTaskFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error updating task');
  }

  public async updateStatus(input: UpdateActionPlanTaskStatusInput) {
    return this.mutate<UpdateActionPlanTaskStatusData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateActionPlanTaskStatus($input: UpdateActionPlanTaskStatusInput!) {
            updateActionPlanTaskStatus(input: $input) {
              errors
              message
              task {
                ${actionPlanTaskFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error updating task status');
  }

  public async reorder(input: ReorderActionPlanTaskInput) {
    return this.mutate<ReorderActionPlanTaskData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation reorderActionPlanTask($input: ReorderActionPlanTaskInput!) {
            reorderActionPlanTask(input: $input) {
              errors
              message
              tasks {
                ${actionPlanTaskFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error reordering task');
  }

  public async delete(input: DeleteActionPlanTaskInput) {
    return this.mutate<null>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation deleteActionPlanTask($input: DeleteActionPlanTaskInput!) {
            deleteActionPlanTask(input: $input) {
              errors
              message
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: null,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error deleting task');
  }

  public async get(variables: GetActionPlanTaskInput) {
    const response = await this.query<GetActionPlanTaskData>(
      () =>
        this.apolloService.executeQuery<GetActionPlanTaskData>({
          query: gql`
            query getActionPlanTask($id: ID!) {
              getActionPlanTask(id: $id) {
                ${actionPlanTaskFields}
              }
            }
          `,
          variables,
        }),
      'Error getting task',
      true
    );

    return response.getActionPlanTask;
  }

  public async getLogs(variables: GetActionPlanTaskInput) {
    const response = await this.query<GetActionPlanTaskData>(
      () =>
        this.apolloService.executeQuery<GetActionPlanTaskData>({
          query: gql`
            query getActionPlanTask($id: ID!) {
              getActionPlanTask(id: $id) {
                logs {
                  ${trailLogFields}
                }
              }
            }
          `,
          variables,
        }),
      'Error getting task logs',
      true
    );

    return response.getActionPlanTask;
  }
}
